import React from 'react';
import {PushNotifications, type PushNotificationSchema} from '@capacitor/push-notifications';
import {Badge} from '@capawesome/capacitor-badge';

import PlatformHelper, {Store} from 'web-app/helpers/platform-helper';
import * as NotificationActions from 'web-app/react/notifications/actions';
import {useOnActions} from 'web-app/react/hooks/use-on-actions';
import {navigationContext} from 'web-app/react/contexts/navigation';
import * as SentryService from 'web-app/services/sentry';
import {type PushNotification} from 'web-app/react/notifications/types';
import {hasValue} from 'web-app/util/typescript';
import RESTClient from 'web-app/api/clients/rest-client-famly-app';

const hasPushNotificationsSupport = () => {
    // On Android, we only support push notifications on apps from Google's Play Store
    return PlatformHelper.isIos() || (PlatformHelper.isAndroid() && PlatformHelper.store() === Store.PLAYSTORE);
};

// This is used to let the backend know from which "app variant" we register push notifications.
// There is a slight change in the backend's flow in Capacitor vs. Cordova.
// See: https://github.com/famly/nuclearfamly/pull/6671 for more info
export enum PushNotificationAppVariant {
    capacitor = 'capacitor',
}

const ignoredErrors = [
    'MISSING_INSTANCEID_SERVICE', // Seems that this can be thrown if Google Play Services is not installed: https://stackoverflow.com/a/39225366
    'SERVICE_NOT_AVAILABLE', // Seems that this is thrown when the device is offline or otherwise can't reach FCM: https://stackoverflow.com/a/53919064
];

/**
 * Registering listeners for push notification related events here.
 *
 * @capacitor/push-notifications also exposes a "removeAllListeners" function.
 * We're not using that, as we on logout essentially reset all event listeners
 * by refreshing the page (the `logOut` function in app/react/session/helpers.ts).
 */
export const initializePush = () => {
    if (!hasPushNotificationsSupport()) {
        return;
    }

    PushNotifications.addListener('registration', event => {
        NotificationActions.didReceiveDeviceToken.dispatch(event.value);
    });

    PushNotifications.addListener('registrationError', error => {
        // We see _a lot_ of transient errors from Android devices. Some of these are expected or unavoidable,
        // so we ignore them in some cases.
        if (PlatformHelper.isAndroid() && ignoredErrors.find(ignored => error.error.includes(ignored))) {
            return;
        }

        SentryService.captureException(
            new Error(`[Push Notification] - on registrationError - push registration failed: ${error.error}`),
            {
                extra: {
                    receivedError: error,
                },
            },
        );
    });

    // This listens for push notifications we receive when the app is opened
    PushNotifications.addListener('pushNotificationReceived', notification => {
        const mappedNotification = mapPushNotificationObject(notification);

        if (!hasValue(mappedNotification)) {
            SentryService.captureMessage(
                '[Push Notification] - on pushNotificationReceived - notification mapping failed',
                {
                    extra: {
                        originalNotification: notification,
                    },
                },
            );
            return;
        }

        NotificationActions.didReceiveNotification.dispatch({
            notification: mappedNotification,
            foreground: true,
        });
    });

    // And this listens for push notifications we receive while the app is _not_ opened.
    PushNotifications.addListener('pushNotificationActionPerformed', action => {
        const mappedNotification = mapPushNotificationObject(action.notification);

        if (!hasValue(mappedNotification)) {
            SentryService.captureMessage(
                '[Push Notification] - on pushNotificationActionPerformed - notification mapping failed',
                {
                    extra: {
                        originalAction: action,
                    },
                },
            );
            return;
        }

        NotificationActions.didReceiveNotification.dispatch({
            notification: mappedNotification,
            foreground: false,
        });
    });

    // Inspired from: https://capacitorjs.com/docs/apis/push-notifications#example
    PushNotifications.checkPermissions().then(checkPermissionsResult => {
        if (checkPermissionsResult.receive === 'granted') {
            PushNotifications.register();
        } else if (checkPermissionsResult.receive === 'prompt') {
            PushNotifications.requestPermissions().then(requestPermissionsResult => {
                if (requestPermissionsResult.receive !== 'granted') {
                    return;
                }

                PushNotifications.register();
            });
        }
    });
};

export const setBadgeCount = (badgeCount: number): Promise<void> => {
    if (!hasPushNotificationsSupport()) {
        return Promise.resolve();
    }

    return Badge.isSupported().then(result => {
        if (!result.isSupported) {
            return Promise.resolve();
        }

        const failSafeBadgeCount = Number.isInteger(badgeCount) ? badgeCount : 0;

        return Badge.set({count: failSafeBadgeCount});
    });
};

export const usePushNotificationHandler = () => {
    const {transitionTo} = React.useContext(navigationContext);

    useOnActions(
        ({payload}) => {
            const {notification, foreground} = payload;

            const notificationId = notification.additionalData?.notificationId;

            if (notificationId) {
                RESTClient.postAuthenticated(`v2/notifications/${notificationId}/read`);
            }

            // Famly was running in the background when notification was pushed
            if (foreground) {
                return;
            }

            const additionalData = notification.additionalData;

            if ('feedItemId' in additionalData) {
                transitionTo('account.post', additionalData.feedItemId);
            } else if ('conversationId' in additionalData) {
                transitionTo('account.inbox', {
                    queryParams: {conversationId: additionalData.conversationId, inboxType: 'OWN'},
                });
            }
        },
        [transitionTo],
        [NotificationActions.didReceiveNotification],
    );
};

const mapPushNotificationObject = (notification: PushNotificationSchema): PushNotification | null => {
    if (PlatformHelper.isIos()) {
        return {
            ...notification,
            additionalData: notification?.data?.aps,
        };
    } else if (PlatformHelper.isAndroid()) {
        return {
            ...notification,
            additionalData: notification?.data,
        };
    } else {
        console.warn(
            "We're not running on a platform that actually supports push notification. Something is wrong here.",
        );
        return null;
    }
};
